import { create } from 'zustand';
import {
  configDb,
  layoutKey,
  type DashboardMode,
} from '@/persistence/config-db';
import type { FilterExpression, FilterOperator } from '@/types/query';

/**
 * Per-slot draft value. Mirrors the shape of a manual {@link FilterExpression}
 * minus the `column`/`source` (which the slot owns).
 *
 * - Text slots use `{ operator: 'in', value: string[] }`; an empty array is
 *   interpreted as "no filter".
 * - Date slots use `{ operator: 'between', value: [from, to] }`; either input
 *   empty is interpreted as "no filter".
 * - Numeric slots use the existing numeric operators; nullary operators
 *   (`is_null`, `is_not_null`) carry `value: null`.
 */
export type SlotDraft = {
  operator: FilterOperator;
  value: FilterExpression['value'];
};

type PinnedFiltersState = {
  /** Ordered list of pinned column names for the active (dataset, mode). */
  columns: string[];
  /** Which dataset `columns`/`drafts` correspond to (null until first load). */
  datasetId: string | null;
  /** Which mode the loaded `columns` belong to (D-020). */
  mode: DashboardMode | null;
  /** True once `loadForDataset` finishes for the current (dataset, mode). */
  loaded: boolean;
  /**
   * Per-column draft state, keyed by column name. `null` draft means "this
   * slot currently has no active filter" (i.e. user cleared it but hasn't
   * applied yet). Missing key means draft matches committed state.
   */
  drafts: Record<string, SlotDraft | null>;

  /** Hydrate `columns` from IndexedDB and seed `drafts` from committed filters. */
  loadForDataset: (
    id: string,
    mode: DashboardMode,
    initialDrafts: Record<string, SlotDraft | null>,
  ) => Promise<void>;

  /** Replace the pinned-column list (and persist). Unknown columns are dropped. */
  setColumns: (next: string[]) => Promise<void>;

  /** Update the draft for a single slot. Pass `null` to clear the slot. */
  setDraft: (column: string, draft: SlotDraft | null) => void;

  /** Reset all drafts to mirror the current committed filter set. */
  resetDrafts: (initial: Record<string, SlotDraft | null>) => void;

  /** Discard in-memory state (e.g. when no dataset is active). */
  clear: () => void;
};

export const usePinnedFiltersStore = create<PinnedFiltersState>((set, get) => ({
  columns: [],
  datasetId: null,
  mode: null,
  loaded: false,
  drafts: {},

  loadForDataset: async (id, mode, initialDrafts) => {
    set({
      loaded: false,
      datasetId: id,
      mode,
      columns: [],
      drafts: initialDrafts,
    });
    let entry: { columns: string[] } | undefined;
    try {
      entry = await configDb.pinnedFilters.get(layoutKey(id, mode));
    } catch {
      entry = undefined;
    }
    // Guard against switching datasets / modes mid-await.
    if (get().datasetId !== id || get().mode !== mode) return;
    set({ columns: entry?.columns ?? [], loaded: true });
  },

  setColumns: async (next) => {
    const id = get().datasetId;
    const mode = get().mode;
    if (!id || !mode) return;
    set({ columns: next });
    try {
      await configDb.pinnedFilters.put({
        key: layoutKey(id, mode),
        datasetId: id,
        mode,
        columns: next,
        updatedAt: Date.now(),
      });
    } catch {
      // Non-fatal: in-memory state persists for the session.
    }
  },

  setDraft: (column, draft) =>
    set((state) => ({ drafts: { ...state.drafts, [column]: draft } })),

  resetDrafts: (initial) => set({ drafts: initial }),

  clear: () =>
    set({
      columns: [],
      datasetId: null,
      mode: null,
      loaded: false,
      drafts: {},
    }),
}));

/**
 * Pull the committed draft state for a column out of the global filters list.
 * Only considers `source: 'manual'` filters so drill-down chips are not
 * conflated with slot state.
 */
export function draftFromFilter(
  filters: readonly FilterExpression[],
  column: string,
): SlotDraft | null {
  const f = filters.find(
    (x) => x.column === column && x.source !== 'drilldown',
  );
  if (!f) return null;
  return { operator: f.operator, value: f.value };
}

/** Build the initial draft map from committed filters for a set of columns. */
export function initialDrafts(
  filters: readonly FilterExpression[],
  columns: readonly string[],
): Record<string, SlotDraft | null> {
  const out: Record<string, SlotDraft | null> = {};
  for (const c of columns) out[c] = draftFromFilter(filters, c);
  return out;
}

/** Decide whether a draft represents "no filter". Empty collections count. */
export function isEmptyDraft(draft: SlotDraft | null): boolean {
  if (!draft) return true;
  const { operator, value } = draft;
  if (operator === 'is_null' || operator === 'is_not_null') return false;
  if (operator === 'in' || operator === 'not_in') {
    return !Array.isArray(value) || value.length === 0;
  }
  if (operator === 'between') {
    if (!Array.isArray(value) || value.length < 2) return true;
    const [a, b] = value;
    // Require BOTH bounds so we never emit `BETWEEN '' AND x` to the worker.
    // For single-sided ranges the user should pick `gte` / `lte` explicitly.
    const aEmpty = a === '' || a === null || a === undefined;
    const bEmpty = b === '' || b === null || b === undefined;
    return aEmpty || bEmpty;
  }
  return value === '' || value === null || value === undefined;
}

/** Deep-equality check for two drafts (committed vs current). */
export function draftsEqual(
  a: SlotDraft | null,
  b: SlotDraft | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return isEmptyDraft(a) && isEmptyDraft(b);
  if (a.operator !== b.operator) return false;
  return JSON.stringify(a.value) === JSON.stringify(b.value);
}
